import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { isValidDate } from '~/features/utils';

// Stored month-precision values look like 'YYYY-MM' (valid ISO 8601).
// Full-precision values look like 'YYYY-MM-DD'.
const isMonthOnly = (raw: unknown): boolean => typeof raw === 'string' && /^\d{4}-\d{2}$/.test(raw);

const formatMMYY = (value: Date): string =>
  new Date(value).toLocaleDateString('en-US', {
    month: '2-digit',
    year: '2-digit',
    timeZone: 'UTC',
  });

const formatMMDDYYYY = (value: Date): string =>
  new Date(value).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });

/** Always MM/YY. Kept for callers that never carry day precision. */
export const formatDateMMYY = (params: ValueFormatterParams): string => {
  if (!params.value || !isValidDate(params.value)) return '--';
  return formatMMYY(params.value);
};

/**
 * Precision-aware formatter for `createDateCol`. Reads the raw stored value
 * from `params.data[field]` to decide: 'YYYY-MM' → MM/YY, 'YYYY-MM-DD' → MM/DD/YYYY.
 * Falls back to MM/YY when the raw string isn't reachable (defensive).
 */
export const formatDateAuto = (params: ValueFormatterParams): string => {
  if (!params.value || !isValidDate(params.value)) return '--';
  const field = params.colDef?.field;
  const raw = field ? params.data?.[field] : undefined;
  return isMonthOnly(raw) ? formatMMYY(params.value) : formatMMDDYYYY(params.value);
};

/**
 * AG Grid date comparator for sorting. Handles null/undefined safely.
 */
export const dateComparator = (valueA: Date | null, valueB: Date | null): number => {
  const timeA = valueA?.getTime();
  const timeB = valueB?.getTime();
  const safeA = Number.isFinite(timeA) ? (timeA ?? 0) : null;
  const safeB = Number.isFinite(timeB) ? (timeB ?? 0) : null;
  if (safeA === null && safeB === null) return 0;
  if (safeA === null) return -1;
  if (safeB === null) return 1;
  return safeA - safeB;
};

/**
 * Factory for a standard date column with precision-aware formatting, filtering,
 * and sorting. A stored 'YYYY-MM' renders as MM/YY; 'YYYY-MM-DD' as MM/DD/YYYY.
 *
 * @param field - The data field name (e.g., 'issueDate', 'expiryDate')
 * @param headerName - The column header label
 */
export const createDateCol = (field: string, headerName: string): ColDef => ({
  field,
  headerName,
  filter: 'agDateColumnFilter',
  valueFormatter: formatDateAuto,
  valueGetter: ({ data }) => {
    if (data?.[field] && isValidDate(data[field])) {
      return new Date(data[field]);
    }
    return null;
  },
  comparator: dateComparator,
});
